<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ conversation: { type: Object, required: true } });
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const show = ref(true);
const candidates = ref([]);
const selectedId = ref('');
const keepId = ref(props.conversation.id);
const assigneeId = ref('');
const loading = ref(true);
const saving = ref(false);
const error = ref('');
const selected = computed(() =>
  candidates.value.find(item => item.id === Number(selectedId.value))
);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const choices = computed(() =>
  selected.value ? [props.conversation, selected.value] : []
);
const close = () => {
  if (!saving.value) emit('close');
};
watch(selectedId, () => {
  keepId.value = props.conversation.id;
  assigneeId.value =
    props.conversation.meta?.assignee?.id || selected.value?.assignee_id || '';
});
onMounted(async () => {
  try {
    const { data } = await ConversationAPI.mergeCandidates(
      props.conversation.id
    );
    candidates.value = data.candidates;
    await store.dispatch('agents/get');
  } catch {
    error.value = t('CONVERSATION.MERGE.LOAD_FAILED');
  } finally {
    loading.value = false;
  }
});
const submit = async () => {
  if (!selected.value || !assigneeId.value || saving.value) return;
  saving.value = true;
  error.value = '';
  const sourceId =
    Number(keepId.value) === props.conversation.id
      ? selected.value.id
      : props.conversation.id;
  try {
    const { data } = await ConversationAPI.merge(
      sourceId,
      Number(keepId.value),
      Number(assigneeId.value)
    );
    window.location.assign(
      `/app/accounts/${store.getters.getCurrentAccountId}/conversations/${data.id}`
    );
  } catch (failure) {
    error.value =
      failure.response?.data?.error || t('CONVERSATION.MERGE.FAILED');
    saving.value = false;
  }
};
const date = value =>
  new Date(value).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
</script>

<template>
  <woot-modal v-model:show="show" :on-close="close">
    <div class="flex flex-col gap-4 p-6 text-n-slate-12">
      <h2 class="text-lg font-semibold">{{ t('CONVERSATION.MERGE.TITLE') }}</h2>
      <p class="text-sm text-n-slate-11">
        {{ t('CONVERSATION.MERGE.DESCRIPTION') }}
      </p>
      <p v-if="loading">{{ t('CONVERSATION.MERGE.LOADING') }}</p>
      <p v-else-if="!candidates.length">{{ t('CONVERSATION.MERGE.EMPTY') }}</p>
      <template v-else>
        <label class="flex flex-col gap-2 text-sm">
          {{ t('CONVERSATION.MERGE.OTHER') }}
          <select
            v-model="selectedId"
            :disabled="saving"
            class="rounded border border-n-weak bg-n-background px-3 py-2"
          >
            <option value="" disabled>
              {{ t('CONVERSATION.MERGE.CHOOSE') }}
            </option>
            <option v-for="item in candidates" :key="item.id" :value="item.id">
              {{
                t('CONVERSATION.MERGE.OPTION', {
                  id: item.id,
                  owner:
                    item.assignee_name || t('CONVERSATION.MERGE.UNASSIGNED'),
                  count: item.message_count,
                  time: date(item.last_activity_at),
                })
              }}
            </option>
          </select>
        </label>
        <template v-if="selected">
          <label class="flex flex-col gap-2 text-sm">
            {{ t('CONVERSATION.MERGE.KEEP') }}
            <select
              v-model="keepId"
              :disabled="saving"
              class="rounded border border-n-weak bg-n-background px-3 py-2"
            >
              <option v-for="item in choices" :key="item.id" :value="item.id">
                {{ `#${item.id}` }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-2 text-sm">
            {{ t('CONVERSATION.MERGE.OWNER') }}
            <select
              v-model="assigneeId"
              :disabled="saving"
              class="rounded border border-n-weak bg-n-background px-3 py-2"
            >
              <option value="" disabled>
                {{ t('CONVERSATION.MERGE.CHOOSE') }}
              </option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>
          <p class="rounded bg-n-amber-3 p-3 text-sm text-n-amber-11">
            {{ t('CONVERSATION.MERGE.WARNING') }}
          </p>
        </template>
      </template>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <div class="flex justify-end gap-2">
        <ButtonV4
          :label="t('CONVERSATION.MERGE.CANCEL')"
          color="slate"
          :disabled="saving"
          @click="close"
        />
        <ButtonV4
          :label="t('CONVERSATION.MERGE.CONFIRM')"
          :disabled="!selected || !assigneeId || loading || saving"
          :is-loading="saving"
          @click="submit"
        />
      </div>
    </div>
  </woot-modal>
</template>
